/**
 * Block 13 PR-G Phase 2 Step 4b — main contract PDF render orchestration.
 *
 * Mirror Block 12 pattern. Uses drawing primitives + font setup из
 * `contractPdfDrawing.ts` (Step 4a). Этот файл содержит только contract-specific
 * section orchestration: placeholder substitution per section ID, requisites
 * table composition, warranty unit alternates resolver.
 *
 * Pipeline:
 *   1. Lazy-load PT Sans Regular + Bold base64 (cached per-session).
 *   2. Compose strings via Step 3 helpers (substituteUnderscores, formatDate,
 *      formatCurrencyDigits, amountInWords) + getCurrencyLabel для PDF-specific
 *      grammar-correct currency word (Phase 7 hotfix round 4, R4-#2).
 *      normalizeApostrophes применяется в `safe()` wrapper внутри Step 4a draw
 *      helpers — на каждом draw call.
 *   3. Draw sections в строгом порядке: Title → DateCity → Preamble →
 *      Sections 1-8 → Section 9 Requisites table → Appendix №1 Spec →
 *      Signature footer → Page numbers (drawn last as cleanup pass).
 *   4. Page breaks через ensureSpace + pdf.addPage() (НЕ через CSS @page).
 *
 * Reference:
 *   - ADR-0025 §Q4 (PDF architecture)
 *   - ADR-0022 «Update 2026-05-23: pivot to jsPDF»
 *   - Block 12 renderQuoteToPdf.ts (pattern source)
 */

import { jsPDF } from 'jspdf';
import type {
  ContractDraft,
  ContractRequisite,
  ContractTemplate,
  ContractTemplateSection,
} from '@pomoshnik/shared';
import { getCurrencyLabel, resolveAdvanceAmount } from '@pomoshnik/shared';

import { sumSpecCost } from '../state/contractSelectors';

import { amountInWords } from './amountInWords';
import {
  composeDateCityLine,
  formatCurrencyDigits,
  formatDate,
  substituteUnderscores,
} from './contractTextRender';
import { replaceCurrencyWordsInText } from './replaceCurrencyWords';
import {
  __resetFontCacheForTests as resetFontCacheImpl,
  drawCenteredLine,
  drawParagraph,
  drawSectionHeading,
  drawSpecBlocks,
  drawTitle,
  drawTwoColumnTable,
  drawPageNumbers,
  FONT_FILES,
  FONT_NAME,
  FONT_SIZES,
  LAYOUT,
  loadFontBase64,
  type RenderCursor,
} from './contractPdfDrawing';

// ── Section-specific paragraph fill ────────────────────────────────────────

/**
 * Cost section (id='cost'): variable paragraph structure across locales.
 * - ru variants (kz.ru, ru.ru, uz.ru): p[0] totalCost+words, p[1] no-placeholder
 *   header («2.2. Оплата производится...»), p[2] advance, p[3] remaining.
 * - kk/uz variants (kz.kk, uz.uz): p[0] totalCost+words, p[1] advance, p[2] remaining.
 *
 * Counting placeholder-paragraphs in order solves both shapes без hardcoded
 * indices.
 */
function fillSectionParagraphs(
  section: ContractTemplateSection,
  draft: ContractDraft,
  template: ContractTemplate,
  specTotal: number,
): string[] {
  // Phase 6.5B Amendment A1 (2026-05-24) — currency code drives PDF render
  // (was: template.meta.currencyCode). User может override currency через
  // chip-segment в SectionCostAndTerms (UI ships в 6.5C), decoupled от
  // template language. Currency word substitution (replaceCurrencyWordsInText)
  // applied к каждому paragraph после fill — иначе template prose всё ещё
  // содержит hardcoded original currency word (kz.ru → «тенге», ru.ru →
  // «рублей», uz.uz → «so'm»), что противоречит draft.currencyCode при override.
  const code = draft.currencyCode;
  const lang = template.meta.lang;
  const templateCode = template.meta.currencyCode;

  // Internal helper: substitute placeholders + apply currency word swap.
  const fillAndSwap = (text: string, values: readonly string[] = []): string => {
    const filled = values.length > 0 ? substituteUnderscores(text, values) : text;
    return replaceCurrencyWordsInText(filled, code, lang, templateCode);
  };

  if (section.id === 'cost') {
    let placeholderIdx = 0;
    return section.paragraphs.map((p) => {
      const count = (p.match(/_+/g) ?? []).length;
      if (count === 0) return fillAndSwap(p);
      let values: string[] = [];
      switch (placeholderIdx) {
        case 0:
          // Phase 6.5G hotfix round 3 (R3-#1) — cost paragraph value sourced
          // от specTotal (auto-propagation from Спецификация), not stale
          // draft.totalCost which is vestigial after read-only UI.
          values = [formatCurrencyDigits(specTotal), amountInWords(specTotal, lang, code)];
          break;
        case 1:
          // PR-D2 (ADR-0040) — эффективный аванс: из ставки «Аванс, %» когда
          // задана, иначе ручной draft.advance (единый расчёт с UI/selectors).
          values = [
            formatCurrencyDigits(
              resolveAdvanceAmount(specTotal, draft.advancePercent, draft.advance),
            ),
          ];
          break;
        case 2: {
          const effectiveAdvance = resolveAdvanceAmount(
            specTotal,
            draft.advancePercent,
            draft.advance,
          );
          const remaining = Math.max(0, specTotal - effectiveAdvance);
          values = [formatCurrencyDigits(remaining)];
          break;
        }
      }
      placeholderIdx++;
      return fillAndSwap(p, values);
    });
  }

  if (section.id === 'terms') {
    return section.paragraphs.map((p, i) =>
      i === 0 ? fillAndSwap(p, [String(draft.durationDays)]) : fillAndSwap(p),
    );
  }

  if (section.id === 'warranty') {
    return section.paragraphs.map((p, i) => {
      if (i !== 0) return fillAndSwap(p);
      const filled = substituteUnderscores(p, [String(draft.warrantyTerm)]);
      const unitResolved = resolveWarrantyUnitAlternates(filled, draft.warrantyUnit, lang);
      return replaceCurrencyWordsInText(unitResolved, code, lang, templateCode);
    });
  }

  // Other sections (subject, rights_obligations, liability, force_majeure,
  // term_final, requisites) — apply currency swap defensively (rare cases
  // где currency word появляется в general prose).
  return section.paragraphs.map((p) => fillAndSwap(p));
}

/**
 * Templates render "месяцев/лет" / "ай/жыл" / "oy/yil" alternative pair —
 * оставляем только unit selected в draft. Per-locale ASCII slash form.
 */
function resolveWarrantyUnitAlternates(
  text: string,
  unit: 'months' | 'years',
  lang: 'ru' | 'kk' | 'uz',
): string {
  if (lang === 'ru') {
    return text.replace(/месяцев\s*\/\s*лет/g, unit === 'months' ? 'месяцев' : 'лет');
  }
  if (lang === 'kk') {
    return text.replace(/ай\s*\/\s*жыл/g, unit === 'months' ? 'ай' : 'жыл');
  }
  // uz
  return text.replace(/oy\s*\/\s*yil/g, unit === 'months' ? 'oy' : 'yil');
}

function fillPreamble(preamble: string, draft: ContractDraft, template: ContractTemplate): string {
  const filled = substituteUnderscores(preamble, [
    draft.customerName,
    draft.executorName,
    draft.executorRepName,
    draft.executorRepBasis,
  ]);
  return replaceCurrencyWordsInText(
    filled,
    draft.currencyCode,
    template.meta.lang,
    template.meta.currencyCode,
  );
}

function fillTitle(title: string, draft: ContractDraft): string {
  // Title типа «ДОГОВОР НА ИЗГОТОВЛЕНИЕ МЕБЕЛИ №___» — без currency word.
  return substituteUnderscores(title, [draft.contractNum]);
}

/**
 * Phase 6.5E Item B — per-locale signature labels.
 *
 * Aibek visual verify round 3: PDF signature section показывал только
 * Заказчик ФИО, Исполнитель — empty, обе стороны inline в одной строке.
 * Root cause: template.signatureFooter (e.g. ru: `"Заказчик: _/ Исполнитель: /"`)
 * имеет underscore-placeholder только для Заказчика — `substituteUnderscores`
 * не подставлял executorName из-за отсутствия `_` после «Исполнитель: ».
 *
 * Fix: bypass template.signatureFooter и рисуем 2 отдельные строки явно из
 * кода. Template JSONs остаются untouched (extract-contract-templates.py не
 * перезапускается — frozen с Phase 1).
 */
const SIGNATURE_LABELS: Record<'ru' | 'kk' | 'uz', { customer: string; executor: string }> = {
  ru: { customer: 'Заказчик', executor: 'Исполнитель' },
  kk: { customer: 'Тапсырыс беруші', executor: 'Орындаушы' },
  uz: { customer: 'Buyurtmachi', executor: 'Ijrochi' },
};

/** Placeholder для пустых ФИО — visual blank line в legal-document convention. */
const SIGNATURE_BLANK = '_______________________';

/**
 * Phase 6.5G hotfix (2026-05-25) — per-locale signature line label под ФИО
 * на странице Приложения №1 (fix #6, Aibek visual verify round 6).
 *
 * Main page имеет «Подпись: _________» в requisites table (template-defined
 * Section 9 «Адреса, реквизиты и подписи Сторон»); Appendix 1 ДО hotfix имел
 * только «Заказчик: ФИО» / «Исполнитель: ФИО» без места для подписи —
 * мебельщик не мог напечатать договор + подписать. Этот константный массив
 * — synthesized signature line (mirror requisites table convention).
 *
 * Variants:
 *   - ru: «Подпись» (kz.ru / ru.ru / uz.ru)
 *   - kk: «Қолы»   (kz.kk)
 *   - uz: «Imzo»   (uz.uz)
 */
const APPENDIX_SIGNATURE_LABEL: Record<'ru' | 'kk' | 'uz', string> = {
  ru: 'Подпись',
  kk: 'Қолы',
  uz: 'Imzo',
};

/** Underscore line для подписи — соответствует convention requisites table. */
const APPENDIX_SIGNATURE_LINE = '_________________';

/**
 * Phase 6.5G hotfix (2026-05-25) — per-locale composed «Приложение №1 к
 * Договору №X от {date}» header (fix #5).
 *
 * Bypass `template.spec.appendixParagraphs[1]` (которое содержит
 * non-substitutable underscore placeholders: `№____ от «___»________202__`)
 * и компонует строку с нуля, используя `formatDate` для consistency с main
 * page Title и preamble. Mirror per-locale word order из соответствующего
 * template paragraph[1].
 *
 * Examples (для contractNum='42', contractDate='2026-05-25'):
 *   ru: «к Договору на изготовление мебели №42 от «25» мая 2026 года»
 *   kk: ««25» мамыр 2026 жылғы №42 Жиһаз дайындауға арналған шартқа»
 *   uz: ««25» may 2026 yil № 42 Mebel tayyorlash shartnomasiga»
 */
function composeAppendixContractRef(
  draft: ContractDraft,
  template: Pick<ContractTemplate, 'meta'>,
): string {
  const lang = template.meta.lang;
  // `formatDate` бросает на invalid/empty contractDate — defensive fallback
  // к literal underscore placeholders (mirror composeDateCityLine pattern).
  let datePart: string;
  try {
    datePart = formatDate(draft.contractDate, lang);
  } catch {
    datePart =
      lang === 'kk'
        ? '«__» __________ 20__ жылғы'
        : lang === 'uz'
          ? '«__» __________ 20__ yil'
          : '«__» __________ 20__ года';
  }
  const num = draft.contractNum.trim() || '____';
  switch (lang) {
    case 'ru':
      return `к Договору на изготовление мебели №${num} от ${datePart}`;
    case 'kk':
      return `${datePart} №${num} Жиһаз дайындауға арналған шартқа`;
    case 'uz':
      return `${datePart} № ${num} Mebel tayyorlash shartnomasiga`;
    default: {
      const _exhaustive: never = lang;
      throw new Error(`composeAppendixContractRef: unsupported lang ${String(_exhaustive)}`);
    }
  }
}

// ── Requisites composition (section 9) ─────────────────────────────────────

/**
 * Section 9 'requisites' template provides 2D string array (label rows like
 * "ИИН/БИН: __________"). Renderer overlays draft values if present, falls back
 * to template literal placeholders otherwise. customerRequisites — left column,
 * executorRequisites — right column.
 */
function composeRequisitesTable(
  template: ContractTemplate,
  draft: ContractDraft,
): readonly string[][] {
  const templateRows = template.requisites.rows;
  if (templateRows.length === 0) return [];

  const customerByKey = mapByKey(draft.customerRequisites);
  const executorByKey = mapByKey(draft.executorRequisites);

  // Header row (ЗАКАЗЧИК / ИСПОЛНИТЕЛЬ) preserved verbatim.
  const result: string[][] = [[...templateRows[0]!]];

  for (const row of templateRows.slice(1)) {
    const left = row[0] ?? '';
    const right = row[1] ?? '';
    result.push([
      overlayRequisiteRow(left, customerByKey),
      overlayRequisiteRow(right, executorByKey),
    ]);
  }
  return result;
}

function mapByKey(requisites: readonly ContractRequisite[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const r of requisites) {
    if (r.value.trim() !== '') m.set(r.key, r.value);
  }
  return m;
}

/**
 * Template row text like "ИИН/БИН: ________________________________" — detect
 * label prefix, look up draft requisite by canonical key, fill underscore tail.
 * Unknown labels / missing values → return verbatim (placeholders remain для
 * manual fill-in print).
 */
function overlayRequisiteRow(templateText: string, valueByKey: Map<string, string>): string {
  const labelMatch = /^([^:_]+)/.exec(templateText);
  if (!labelMatch) return templateText;
  const label = labelMatch[1]!.trim().toLowerCase();
  const candidateKey = labelToKey(label);
  if (!candidateKey) return templateText;
  const value = valueByKey.get(candidateKey);
  if (value === undefined || value.trim() === '') return templateText;
  return substituteUnderscores(templateText, [value]);
}

/**
 * Maps template label (lowercased) to canonical requisite key. Conservative —
 * unknown labels return null, template text preserved verbatim. Supports
 * common variants для 3 locales.
 */
function labelToKey(label: string): string | null {
  if (/фио|наименование|name|atı|aty/.test(label)) return 'name';
  if (/адрес|address|manzil|mekenjayı/.test(label)) return 'address';
  if (/иин|бин|inn|bin|iin/.test(label)) return 'iin_bin';
  if (/телефон|phone|tel/.test(label)) return 'phone';
  if (/иик|расчет|счет|account/.test(label)) return 'iik';
  if (/банк|bank/.test(label)) return 'bank';
  if (/бик|bik|mfo/.test(label)) return 'bik';
  if (/пинфл|pinfl/.test(label)) return 'pinfl';
  if (/инн/.test(label)) return 'inn';
  if (/кпп|kpp/.test(label)) return 'kpp';
  if (/огрн|ogrn/.test(label)) return 'ogrn';
  return null;
}

// ── Main render entry point ────────────────────────────────────────────────

export async function renderContractToPdf(
  draft: ContractDraft,
  template: ContractTemplate,
): Promise<Blob> {
  const [regularBase64, boldBase64] = await Promise.all([
    loadFontBase64('regular'),
    loadFontBase64('bold'),
  ]);

  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  pdf.addFileToVFS(FONT_FILES.regular.vfs, regularBase64);
  pdf.addFont(FONT_FILES.regular.vfs, FONT_NAME, 'normal');
  pdf.addFileToVFS(FONT_FILES.bold.vfs, boldBase64);
  pdf.addFont(FONT_FILES.bold.vfs, FONT_NAME, 'bold');
  pdf.setFont(FONT_NAME, 'normal');

  const cursor: RenderCursor = { y: LAYOUT.marginTop };

  // Phase 6.5G hotfix round 3 (R3-#1) — single source of truth для Суммы:
  // delegates к `sumSpecCost` (shared с UI selectTotalSpec / ContractCard).
  // Used в cost section paragraphs (Sum, amountInWords, remaining) AND
  // в Appendix №1 bottom Итого — unified, no decoupling.
  const specTotal = sumSpecCost(draft.spec);

  // 1. Title.
  drawTitle(pdf, cursor, fillTitle(template.title, draft));

  // 2. DateCity line — composed (template.dateCityLine has empty «» literal).
  drawCenteredLine(pdf, cursor, composeDateCityLine(template, draft), FONT_SIZES.body);
  cursor.y += LAYOUT.paragraphGap;

  // 3. Preamble.
  drawParagraph(pdf, cursor, fillPreamble(template.preamble, draft, template));
  cursor.y += LAYOUT.paragraphGap;

  // 4. Sections 1-8 (skip section 9 requisites, drawn as table).
  for (const section of template.sections) {
    if (section.id === 'requisites') continue;
    drawSectionHeading(pdf, cursor, section.number, section.heading);
    const filled = fillSectionParagraphs(section, draft, template, specTotal);
    for (const paragraph of filled) {
      drawParagraph(pdf, cursor, paragraph);
    }
    // Phase 6.5C Aibek decision #7 + ADR Amendment A3 — render costCustomRows
    // как part of cost section (Срок гарантии нестандартный / особые условия).
    // Empty label rows скипаются (UX trap protection — user видит в UI но не
    // в PDF). Currency word swap applied к value defensively (verbose 4-arg
    // call preserved per supplement Section 1 LOW#2).
    if (section.id === 'cost' && draft.costCustomRows.length > 0) {
      for (const row of draft.costCustomRows) {
        if (row.label.trim() === '') continue;
        const swappedValue = replaceCurrencyWordsInText(
          row.value,
          draft.currencyCode,
          template.meta.lang,
          template.meta.currencyCode,
        );
        drawParagraph(pdf, cursor, `${row.label}: ${swappedValue}`);
      }
    }
    cursor.y += LAYOUT.sectionGap - LAYOUT.paragraphGap;
  }

  // 5. Section 9 — Requisites table.
  const requisitesSection = template.sections.find((s) => s.id === 'requisites');
  if (requisitesSection) {
    drawSectionHeading(pdf, cursor, requisitesSection.number, requisitesSection.heading);
    drawTwoColumnTable(pdf, cursor, composeRequisitesTable(template, draft), {
      headerRow: true,
    });
    cursor.y += LAYOUT.sectionGap;
  }

  // 6. Appendix №1 Спецификация (new page). Phase 6.5F.3 — per-block tables
  // (Название | Тип | Бренд per A7 redesign, см. Excel template-spec-pdf.png).
  // template.spec.columns DROPPED — header labels hardcoded в drawSpecBlocks
  // (Aibek decision 2026-05-25). appendixParagraphs остаются (intro «Приложение
  // №1 — Спецификация мебели...» legal text); currency word swap применяется
  // defensively.
  //
  // **Phase 6.5G hotfix (2026-05-25) — fix #5** (Aibek visual verify round 6):
  // appendixParagraphs[1] (контрактная reference с unsubstituted underscores
  // `№____ от «___»________202__`) bypass'ится — вместо неё рендерится
  // composeAppendixContractRef(draft) с substituted contractNum + formatDate.
  // appendixParagraphs[0] (title literal «ПРИЛОЖЕНИЕ № 1») + [2]
  // («СПЕЦИФИКАЦИЯ») рендерятся verbatim.
  pdf.addPage();
  cursor.y = LAYOUT.marginTop;
  template.spec.appendixParagraphs.forEach((ap, idx) => {
    const text =
      idx === 1
        ? composeAppendixContractRef(draft, template)
        : replaceCurrencyWordsInText(
            ap,
            draft.currencyCode,
            template.meta.lang,
            template.meta.currencyCode,
          );
    drawCenteredLine(pdf, cursor, text, FONT_SIZES.body);
  });
  cursor.y += LAYOUT.sectionGap;

  if (draft.spec.length === 0) {
    // Defensive fallback: createInitialContractDraft + HYDRATE reducer guard
    // auto-seed ≥1 block, поэтому empty spec ожидаемо только в legacy/test
    // edge cases. Render template.spec.defaultRows как plain paragraphs (без
    // table layout) — preserves intent «show template defaults» without
    // duplicating drawSpecBlocks shape.
    for (const row of template.spec.defaultRows) {
      const text = row.filter((c) => c.trim() !== '').join(' | ');
      if (text === '') continue;
      drawParagraph(pdf, cursor, text);
    }
  } else {
    drawSpecBlocks(pdf, cursor, draft.spec, draft.currencyCode);
  }
  cursor.y += LAYOUT.sectionGap;

  // 7. Total cost row (appendix bottom). Phase 6.5G hotfix round 3 (R3-#1) —
  // reuses specTotal computed at top of render (single source of truth).
  // Phase 7 hotfix round 4 (R4-#2, 2026-05-26) — currency word («тенге»/
  // «рублей»/«сум») вместо symbol (₸/₽) per Aibek visual verify round 9.
  // `getCurrencyLabel` (НЕ `getCurrencyWord`) даёт grammar-correct genitive
  // plural для RUB. Главная страница Сумма НЕ trogается (прописная сумма
  // уже включает word внутри amountInWords).
  drawParagraph(
    pdf,
    cursor,
    `Итого: ${formatCurrencyDigits(specTotal)} ${getCurrencyLabel(draft.currencyCode)}`,
    { bold: true },
  );

  // 8. Signature section — Phase 6.5E Item B — explicit 2 paragraphs
  // (Заказчик / Исполнитель) instead of inline template.signatureFooter
  // substitution which dropped executorName. See SIGNATURE_LABELS comment.
  //
  // **Phase 6.5G hotfix (2026-05-25) — fix #6** (Aibek visual verify round 6):
  // дополнительно к «Заказчик: ФИО» / «Исполнитель: ФИО» рендерим место для
  // подписи — «{Подпись/Қолы/Imzo}: _________________» under each ФИО. Mirror
  // requisites table convention где Section 9 уже содержит «Подпись:» rows
  // per-locale. Без этих строк мебельщик не имел места поставить подпись на
  // печатной Приложения 1 (signature lines были только на main page Section 9).
  const labels = SIGNATURE_LABELS[template.meta.lang];
  const signatureLineLabel = APPENDIX_SIGNATURE_LABEL[template.meta.lang];
  const customerSignature = draft.customerName.trim() || SIGNATURE_BLANK;
  const executorSignature = draft.executorName.trim() || SIGNATURE_BLANK;
  cursor.y += LAYOUT.sectionGap;
  drawParagraph(pdf, cursor, `${labels.customer}: ${customerSignature}`, { bold: true });
  drawParagraph(pdf, cursor, `${signatureLineLabel}: ${APPENDIX_SIGNATURE_LINE}`);
  drawParagraph(pdf, cursor, `${labels.executor}: ${executorSignature}`, { bold: true });
  drawParagraph(pdf, cursor, `${signatureLineLabel}: ${APPENDIX_SIGNATURE_LINE}`);

  // 9. Page numbers (drawn last as cleanup over all pages).
  drawPageNumbers(pdf);

  return pdf.output('blob');
}

/** Reset cache — re-exposed для unit-test isolation. */
export const __resetFontCacheForTests = resetFontCacheImpl;
